import { createContext, useContext } from 'react';
import { NO_CONNECTION, type XeroConnection } from './xero';

export interface XeroContextValue {
  connection: XeroConnection;
  /** True until the first read settles. */
  loading: boolean;
  /**
   * Why the connection could not be read, in words a person can act on.
   *
   * Null while the endpoint simply says "not connected" — that is a normal
   * state, not a failure, and must never render as an error.
   */
  error: string | null;
  /**
   * True when the API has no `/xero` routes at all yet (404).
   *
   * Kept separate from `error` because the two want opposite tones: a 404 here
   * means the backend has not shipped its half, which is a calm "not set up"
   * state, while a 500 means something broke and someone should look.
   */
  notInstalled: boolean;
  reload: () => void;
}

// Kept in its own module so the provider component and the hook can both
// import it without breaking react-refresh's component-only-export rule —
// the same split as `auth-context.ts`.
export const XeroContext = createContext<XeroContextValue | null>(null);

/**
 * The Xero connection, read once and shared.
 *
 * Deliberately falls back to a disconnected reading rather than throwing when
 * no provider is mounted. Every detail page in the app can host a
 * `<XeroPanel>`, and a missing provider should degrade to "Xero is not
 * connected" — which is both true and harmless — rather than blanking a page
 * that was otherwise working.
 */
export function useXero(): XeroContextValue {
  return (
    useContext(XeroContext) ?? {
      connection: NO_CONNECTION,
      loading: false,
      error: null,
      notInstalled: false,
      reload: () => {},
    }
  );
}
